var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import debounce from 'lodash.debounce';
import mergeRefs from 'merge-refs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSwipeable } from '@snack-uikit/utils';
import { TEST_IDS } from '../../testIds';
import styles from './styles.module.css';
export function ItemProvider({ items, showItems, scrollBy, slideCallback, transition, swipe, swipeActivateLength, page, gap, }) {
    const containerRef = useRef(null);
    const [computedValues, setComputedValues] = useState({
        itemWidth: 0,
        gap: 0,
    });
    const recalculateItemsSize = useCallback(() => {
        const containerElement = containerRef.current;
        if (!containerElement) {
            return;
        }
        const styles = getComputedStyle(containerElement);
        const gap = Number.parseFloat(styles.getPropertyValue('--gap'));
        const paddingLeft = Number.parseFloat(styles.getPropertyValue('padding-left'));
        const paddingRight = Number.parseFloat(styles.getPropertyValue('padding-right'));
        const itemWidth = (containerElement.getBoundingClientRect().width -
            (Math.trunc(showItems) - 1) * gap -
            paddingLeft -
            paddingRight) /
            showItems;
        setComputedValues({ itemWidth, gap });
    }, [showItems]);
    useEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return;
        }
        recalculateItemsSize();
        const observer = new ResizeObserver(debounce(recalculateItemsSize, 100));
        observer.observe(node);
        return () => observer.disconnect();
    }, [recalculateItemsSize]);
    const itemsTrackerRef = useRef(null);
    const hideNonVisibleItems = () => {
        var _a;
        (_a = itemsTrackerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`[data-test-id=${TEST_IDS.trackItem}]`).forEach(slide => {
            slide.setAttribute('aria-hidden', '0');
            slide.querySelectorAll('a, button, select, input, textarea, [tabindex="0"]').forEach(focusableElement => {
                focusableElement.setAttribute('tabindex', '-5');
                focusableElement.classList.add(styles.hiddenItem);
            });
        });
    };
    const showVisibleItems = (slide, page, show) => {
        var _a;
        (_a = itemsTrackerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`[data-test-id=${TEST_IDS.trackItem}]`).forEach((item, i) => {
            if (i >= page * slide && i < page * slide + Math.trunc(show)) {
                item.removeAttribute('aria-hidden');
                item.querySelectorAll('a, button, select, input, textarea, [tabindex="-5"]').forEach(focusableElement => {
                    focusableElement.setAttribute('tabindex', '0');
                    focusableElement.classList.remove(styles.hiddenItem);
                });
            }
        });
    };
    const transform = useMemo(() => Number(-(page * scrollBy * computedValues.itemWidth + computedValues.gap * page * scrollBy)), [computedValues.gap, computedValues.itemWidth, page, scrollBy]);
    useEffect(() => {
        hideNonVisibleItems();
        showVisibleItems(scrollBy, page, showItems);
    }, [page, scrollBy, showItems]);
    const handleSlideClick = useCallback(e => {
        var _a, _b;
        if (showItems === 1) {
            return;
        }
        const slideRect = e.currentTarget.getBoundingClientRect();
        const containerRect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const containerViewport = (_b = containerRect === null || containerRect === void 0 ? void 0 : containerRect.right) !== null && _b !== void 0 ? _b : 0;
        const slidePositionDelta = containerViewport - slideRect.right;
        const minPos = Math.min(slidePositionDelta, slideRect.x);
        if (minPos < 0) {
            slideCallback(slidePositionDelta);
        }
    }, [showItems, slideCallback]);
    const itemWidth = useMemo(() => {
        if (computedValues.itemWidth > 0) {
            return computedValues.itemWidth;
        }
        const fallbackWidth = Math.floor(100 / showItems);
        return `${fallbackWidth}%`;
    }, [computedValues.itemWidth, showItems]);
    const [drag, setDrag] = useState({ drag: 0, pointers: true });
    const handleSwiping = eventData => {
        setDrag({ drag: eventData.deltaX, pointers: Math.abs(eventData.deltaX) < Number.MIN_SAFE_INTEGER });
    };
    const handleSwiped = eventData => {
        if (Math.abs(drag.drag) < swipeActivateLength) {
            return setDrag({ drag: 0, pointers: true });
        }
        slideCallback(eventData.dir === 'Left' ? -1 : 1);
        setDrag({ drag: 0, pointers: true });
        return;
    };
    const _a = useSwipeable({
        onSwiping: handleSwiping,
        onSwiped: handleSwiped,
        trackMouse: true,
        enabled: swipe,
        availableDirections: ['Left', 'Right'],
    }), { ref } = _a, swipeProps = __rest(_a, ["ref"]);
    return (_jsx("div", { ref: containerRef, className: styles.itemProvider, "data-pointers": !drag.pointers || undefined, "data-swipe": swipe || undefined, "data-gap": gap, style: Object.assign({}, (gap ? { '--gap': gap } : {})), children: _jsx("div", Object.assign({}, swipeProps, { className: styles.itemTracker, "data-test-id": TEST_IDS.trackLine, style: {
                transform: `translateX(${transform + drag.drag}px)`,
                transition: `transform ${transition}s ease 0s`,
            }, ref: mergeRefs(ref, itemsTrackerRef), children: items.map((item, i) => (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            _jsx("div", { style: { width: itemWidth }, className: styles.itemContainer, role: 'group', "data-test-id": TEST_IDS.trackItem, onClick: handleSlideClick, children: item }, i))) })) }));
}
